import { Component, HostListener, OnDestroy, OnInit } from '@angular/core'
import { Subscription } from 'rxjs'
import { BrandSelectionService } from '../services/ui/brand-selection.service'
import { BrandTokenInspectorService } from '../services/ui/brand-token-inspector.service'

interface Box { top: number, left: number, width: number, height: number }

/** The editor's own chrome is never a target. */
const IGNORED_HOSTS = 'app-brand-inspector, app-style-configurator, ion-menu, .cdk-overlay-container'

/**
 * Draws the outline over whatever the pointer is on while picking, and hands the click to the
 * inspector in the side panel. It renders nothing else, so there is no floating panel to
 * position and nothing that can land off-screen.
 */
@Component({
    selector: 'app-brand-inspector',
    templateUrl: './brand-inspector.component.html',
    styleUrls: ['./brand-inspector.component.scss'],
})
export class BrandInspectorComponent implements OnInit, OnDestroy {

    picking = false
    highlight: Box = null
    /** Names of the colours found, shown as a hint next to the outline. */
    hoveredLabels: string[] = []

    constructor(
        private inspector: BrandTokenInspectorService,
        private selectionService: BrandSelectionService,
    ) { }

    ngOnInit(): void {
        this.subscription = this.selectionService.picking.subscribe(picking => {
            this.picking = picking
            if (!picking) this.clearHighlight()
        })

        document.addEventListener('click', this.onDocumentClick, { capture: true })
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe()
        document.removeEventListener('click', this.onDocumentClick, { capture: true })
    }

    @HostListener('document:mousemove', ['$event'])
    onMouseMove(event: MouseEvent): void {
        if (!this.picking || this.pending) return

        this.pending = true
        requestAnimationFrame(() => {
            this.pending = false
            this.track(event.clientX, event.clientY)
        })
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.picking) this.selectionService.stopPicking()
    }

    private subscription: Subscription
    private pending = false

    /** While picking, a click picks a colour. It must never navigate or submit anything. */
    private readonly onDocumentClick = (event: MouseEvent): void => {
        if (!this.picking) return

        const target = event.target as Element
        if (target?.closest(IGNORED_HOSTS)) return

        event.preventDefault()
        event.stopPropagation()

        // Resolved from the click target, not from the hover state, which is computed on the
        // next frame and would still be empty for a click arriving with the movement.
        const themed = this.inspector.closestThemed(target)
        if (!themed) return

        // The selection offers more than the hover hint: the colours around the element too.
        this.selectionService.select(themed.element, this.inspector.tokensForSelection(themed.element))
    }

    private track(x: number, y: number): void {
        const target = document.elementFromPoint(x, y)

        if (!target) {
            this.clearHighlight()
            return
        }

        if (target.closest(IGNORED_HOSTS)) return

        const themed = this.inspector.closestThemed(target)

        if (!themed) {
            this.clearHighlight()
            return
        }

        const rect = themed.element.getBoundingClientRect()
        this.highlight = { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
        this.hoveredLabels = themed.tokens.map(t => t.token)
    }

    private clearHighlight(): void {
        this.highlight = null
        this.hoveredLabels = []
    }
}
